import React, { useEffect, useState, memo } from "react";
import {
    TextField, Button, Grid, Box, Switch, FormControlLabel, FormControl, Dialog, DialogTitle, DialogActions, DialogContent,
    InputAdornment, Paper, Typography, Card, Divider, Link, DialogContentText
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../../Context/Contexts";
import CenteredCard from "../../../components/Card/CenteredCardLayout";
import InfoButton from "../../../components/Tooltip/InfoTextField";
import { ItemImportViewModel, CsvMiniModelViewModel, CsvMiniModelVariantViewModel, ProgressResultViewModel, ListQuery } from "../../../Types/ExportTypes";
import DefaultActions from "../../../components/Card/DefaultActions";
import { StatNoAutoComplete, CountryAutoComplete, SupplierAutoComplete, CurrencyAutoComplete } from "../../../components/AutoComplete/AutoCompletes";
import { useTranslation } from "react-i18next";
import LocalizedNumberInputs from "../../../components/Input/LocalizedNumberInputs"
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteIcon from "@mui/icons-material/Delete";
import ImageBox from "../../../components/Image/ImageBox";
import CheckCircle from "@mui/icons-material/CheckCircle";
import ListAltIcon from "@mui/icons-material/ListAlt";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ResponsivePagination from "../../../components/ResponsivePagination";
const ItemImportEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
    const { t } = useTranslation();
    const { id = 0, progressId = 0, imported = false } = params || {};
    const [isLoading, setIsLoading] = useState(false);
    const { showLoading, hideLoading } = useLoading();
    const { authService } = useAuth();
    const { showSnackbar } = useSnackbar();
    const { goBack, navigate } = useNavigation();
    const [viewModel, setViewModel] = useState<ItemImportViewModel | null>(null);
    const [importLines, setImportLines] = useState<CsvMiniModelViewModel[]>([])
    const [pendingFile, setPendingFile] = useState<File | null>(null);
    const [searchFilter, setSearchFilter] = useState<string>("");
    const [query, setQuery] = useState<ListQuery>({
        page: 0,
        pageSize: 10,
        filter: "",
    });
    const [totalCount, setTotalCount] = useState(0);
    const [editingLine, setEditingLine] = useState<CsvMiniModelViewModel | null>(null);
    const [showMiscDescriptions, setShowMiscDescription] = useState(false);
    const [showCustoms, setShowCustoms] = useState(false);
    const [showOnlyErrorLines, setShowOnlyErrorLines] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [showVariants, setShowVariants] = useState(false);
    const [showSuccesDialog, setShowSuccessDialog] = useState(false);
    const [showMorePrices, setShowMorePrices] = useState(false);

    useEffect(() => {
        if (isLoading) showLoading();
        else hideLoading();
    }, [isLoading, showLoading, hideLoading]);

    useEffect(() => {
        if (!id || id == 0) {

            const now = new Date();
            const description = `Import_${now.toISOString().slice(0, 10)} ${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
            setViewModel({ createNew: true, description });
            return;
        }

        setIsLoading(true);
        authService
            .get<ItemImportViewModel>(`/itemApi/GetImport?importId=${id}`)
            .then((data) => {
                setViewModel(data);
                if (imported == true || imported == "true") fetchImportLines();
            })
            .catch((error: any) => {
                showSnackbar(`${t("common.failedToFetchEntries")}: ${error.response?.data ?? error.message}`, "error");
            })
            .finally(() => setIsLoading(false));
    }, [id, imported, progressId, authService]);


    useEffect(() => {
        const newFilter = showOnlyErrorLines ? "Error" : "";
        if (query.filter !== newFilter) {
            setQuery((prev) => ({ ...prev, pageSize: 10, page: 0, filter: newFilter }));
        }
    }, [showOnlyErrorLines]);


    //const handleSave = async () => {
    //    if (!viewModel) return;
    //    if (!validateViewModel()) return; // Prevent save if validation fails
    //    var newItem = viewModel.createNew ?? false;

    //    setIsLoading(true);
    //    try {
    //        const response = await authService.post<ItemImportViewModel>("/itemApi/UpdateItemImport", viewModel);
    //        setViewModel(response);
    //        showSnackbar(t("dialog.savedSuccessfully"), "success");

    //        if (newItem) {
    //            navigate({ path: "/purchase/item/import/edit", params: { id: response.id } });
    //        }
    //    } catch {
    //        showSnackbar(t("dialog.saveErrorGeneric"), "error");
    //    } finally {
    //        setIsLoading(false);
    //    }
    //};

    const handleChange = (field: keyof ItemImportViewModel, value: any) => {
        setViewModel((prev) => (prev ? { ...prev, [field]: value } : null));
    };



    const handleDelete = async () => {

        const isConfirmed = window.confirm(t("dialog.deleteEntityConfirm"));
        if (!isConfirmed) return;
        var newItem = viewModel?.createNew ?? false;
        if (newItem) {
            navigate({ path: "/purchase/item/import/list" });
            return;
        }

        setIsLoading(true);
        try {
            await authService.post(`/itemApi/DeleteItemImport?importId=${viewModel?.id}`, {});
            showSnackbar(t("dialog.deletedSuccessfully"), "success");

            navigate({ path: "/purchase/item/import/list" });
        } catch (error: any) {
            showSnackbar(`${t("dialog.deleteFailed")} : ${error.response?.data ?? error.message}`, "error");

        } finally {
            setIsLoading(false);
        }
    };

    const validateViewModel = () => {
        if (!viewModel) {
            showSnackbar(t("dialog.dataMissing"), "error");
            return false;
        }

        if (!viewModel.description || viewModel.description.trim() === "") {
            showSnackbar(t("help.descriptionRequired"), "warning");
            return false;
        }
        if (pendingFile === null || pendingFile === undefined) {
            showSnackbar(t("help.invalidFile"), "warning");
            return false;
        }


        return true;
    };



    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return "";

        setPendingFile(file);

        return "";
    };


    const handleImport = async () => {
        if (!validateViewModel()) return;
        setIsLoading(true);
        const formData = new FormData();
        if (pendingFile != null) formData.append("file", pendingFile);
        formData.append("viewModel", new Blob([JSON.stringify(viewModel)], { type: "application/json" }));
        try {
            const response = await authService.post<{ importId: number, progressId: number }>("/itemApi/CreateImportItem", formData);
            await pollStatus(response.importId, response.progressId, false,false);
        } catch (error) {
            console.error("Error uploading file:", error);
            showSnackbar(t("dialog.fileUploadError"), "error");
        } finally {
            setIsLoading(false);
        }
    }

    const pollStatus = async (importId: number, progressId: number, clearCache: boolean,isImportFromPreview : boolean) => {
        const interval = 5000;
        let attempts = 100000;

        while (attempts > 0) {
            try {
                const response = await authService.get<ProgressResultViewModel>(`/progressApi/GetProgress?progressId=${progressId}`);

                if (!response) throw new Error("Failed to fetch status");
                if (response.error) {
                    showSnackbar(`${response.text} - ${response.progress} %`, "error");
                    if (!viewModel?.createNew) fetchImportLines();
                    break;
                }

                if (response.text) {
                    showSnackbar(`${response.text} - ${response.progress} %`, "info");
                }
                if (response.done) {
                    if (clearCache) await authService.post(`/itemApi/ClearItemCache?progressId=${progressId}`, {})
                    showSnackbar(t("dialog.itemImportSuccess","success"));
                    //if it isnt the import i.e create or update we navigate with info of progress and importId, otherwise we show dialog
                    //that its complete
                    if (!isImportFromPreview) navigate({ path: "/purchase/item/import/edit", params: { id: importId, progressId: progressId, imported: true } });

                    if (isImportFromPreview) {
                        setIsCompleted(true);
                        setShowSuccessDialog(true);
                    }

                    if (!viewModel?.createNew) fetchImportLines();
                    break;
                }

            } catch (error) {
                console.error("Error fetching status:", error);
                showSnackbar(t("dialog.fetchStatusError"), "error");
                return;
            }

            attempts--;
            await new Promise((resolve) => setTimeout(resolve, interval));
        }
    };

    const fetchImportLines = async (importIdOverride?: number | null) => {
        var importId = importIdOverride ?? id;

        if (importId === null || importId === undefined) return;
        showLoading();
        try {
            const response = await authService.post<{ items: CsvMiniModelViewModel[], count: number }>(
                `/itemApi/GetImportLines?importId=${importId}`,
                query
            );
            setImportLines(prev => {
                //const fieldErrorMap: Record<string, string> = {
                //    no: "Item number is required",
                //    description: "Description cannot be empty",
                //    description2: "Secondary description too long",
                //    fullDescription: "Full description contains invalid characters",
                //    barcode: "Barcode is invalid or duplicated",
                //    supplier: "Supplier must be specified",
                //    supplierItemNo: "Supplier item number is missing",
                //    salesPrice: "Sales price must be greater than zero",
                //    salesPriceCurrency: "Invalid sales price currency code",
                //    purchasePrice: "Purchase price must be a positive number",
                //    purchasePriceCurrency: "Invalid purchase price currency code",
                //    colourGroup: "Colour group does not exist",
                //    colour: "Colour must be selected",
                //    sizeGroup: "Size group is invalid",
                //    size: "Size is not recognized",
                //    lengthGroup: "Length group is missing",
                //    length: "Length must be provided",
                //    itemCategory: "Item category is required",
                //    productGroup: "Product group not found",
                //    itemGroup: "Item group is invalid",
                //    program: "Program code is incorrect",
                //    collection: "Collection name is invalid",
                //    gender: "Gender must be Male, Female or Unisex",
                //    b2C: "Invalid B2C flag",
                //    b2B: "Invalid B2B flag",
                //    netWeight: "Net weight must be a positive number",
                //    grossWeight: "Gross weight must be greater than net weight",
                //    volume: "Volume must be greater than zero",
                //    statNo: "Statistical number is required",
                //    countryOfOrigin: "Invalid country code",
                //    stock: "Stock must be a whole number"
                //};

                //const generateErrors = () =>
                //    Object.entries(fieldErrorMap)
                //        .filter(() => Math.random() < 0.3) // ~30% chance to include each error
                //        .map(([field, error]) => ({ field, error }));

                //const enriched = response.items.map(item => {
                //    if (!item.errors || item.errors.length === 0) {
                //        item.errors = generateErrors();
                //    }

                //    if (item.errors?.length > 0) item.error = true;
                //    item.variants = item.variants?.map(variant => ({
                //        ...variant,
                //        errors: variant.errors?.length ? variant.errors : generateErrors()
                //    })) ?? [];

                //    return item;
                //});

                setTotalCount(response.count);
                //return enriched;
                return response.items;
            });

            const allCompleted =
                response.items.length > 1 &&
                response.items.every(line => line.completed);

            if (allCompleted) {
                showSnackbar(t("dialog.itemImportSuccess"), "success");

                setTimeout(() => {
                    navigate({ path: "/purchase/item/list" });
                }, 5000);
            }

        } catch (error) {
            console.error("Failed to fetch documents:", error);
        } finally {
            hideLoading();
        }
    };


    useEffect(() => {
        fetchImportLines();
    }, [query]);

    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setQuery({ ...query, filter: searchFilter, page: 0 });
    };

    const handlePageChange = (newPage: number) => {
        setQuery({ ...query, page: newPage });
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setQuery({ ...query, pageSize: newPageSize, page: 0 });
    };



    const handleFieldChange = async (field: keyof CsvMiniModelViewModel, value: any) => {
        if (editingLine) {
            let updatedLine = { ...editingLine, [field]: value }



            setEditingLine((prev) => {
                if (!prev) return null;
                updatedLine = { ...prev, [field]: value, };
                return updatedLine;
            });


        };
    }

    const handleVariantFieldChange = (
        line: CsvMiniModelViewModel,
        variantIndex: number,
        field: keyof CsvMiniModelVariantViewModel,
        value: any
    ) => {
        if (!line.variants) return;

        const updatedVariants = [...line.variants];
        const originalVariant = updatedVariants[variantIndex];

        updatedVariants[variantIndex] = {
            ...originalVariant,
            [field]: value,
        };

        setEditingLine((prev) => {
            if (!prev) return null;
            return {
                ...prev,
                variants: updatedVariants,
            };
        });
    };

    const handleSaveLine = async () => {

        if (!editingLine) return;
        const currentLine = editingLine;

        if (currentLine.completed) {
            showSnackbar(t("dialog.cannotChangeCompletedLine"), "warning");
            return;
        }

        try {
            setIsLoading(true);
            const updatedLine = await authService.post<CsvMiniModelViewModel>(`itemApi/CreateOrUpdateImportLine?importId=${viewModel?.id}`, currentLine);
            if (currentLine.id && currentLine.id !== 0) {
                setImportLines((prev) =>
                    prev.map((line) => (line.id === updatedLine.id ? updatedLine : line))
                );
            }
            showSnackbar(t("dialog.lineSaved"), "success");
            setEditingLine(null);
        } catch (error: any) {
            console.error("Error saving line:", error);
            showSnackbar(`${t("dialog.lineSaveError")} : ${error.response?.data ?? error.message}`, "error");

        } finally {
            setIsLoading(false);
        }
    };


    const handleDeleteLine = async (deleteLine?: CsvMiniModelViewModel | null) => {

        if (!deleteLine) return;
       
        const isConfirmed = window.confirm(t("dialog.deleteEntityConfirm"));
        if (!isConfirmed) return;

        if (deleteLine.completed) {
            showSnackbar(t("dialog.cannotChangeCompletedLine"), "warning");
            return;
        }

        setIsLoading(true);
        try {
            await authService.post<CsvMiniModelViewModel>(`itemApi/DeleteImportLine?importId=${viewModel?.id}`, deleteLine);
            showSnackbar(t("dialog.deletedSuccessfully"), "success");
            setImportLines((prev) => prev.filter((line) => line.id !== deleteLine.id));
            setEditingLine(null);
        } catch (error: any) {
            showSnackbar(`${t("dialog.deleteFailed")} : ${error.response?.data ?? error.message}`, "error");

        } finally {
            setIsLoading(false);
        }
    };

    const handleImportFromPreview = async () => {


        const allCompleted =
            importLines.length > 1 &&
            importLines.every(line => line.completed);
        if (isCompleted || allCompleted) {
            showSnackbar(t("dialog.noLinesToHandle"), "warning");
            return;
        }
        setIsLoading(true);
        try {
            const response = await authService.post<{ importId: number, progressId: number }>(`/itemApi/ImportFromPreview?importId=${viewModel?.id}`, {});
            await pollStatus(response.importId, response.progressId, true,true);
        } catch (error) {
            console.error("Error uploading import:", error);
            showSnackbar(t("dialog.fileUploadError"), "error");
        } finally {
            setIsLoading(false);
        }
    }

    const getError = (field: string) =>
        editingLine?.errors?.find((e) => e.field?.toLowerCase() === field.toLowerCase())?.error;

    const hasError = (field: string) =>
        editingLine?.errors?.some((e) => e.field?.toLowerCase() === field.toLowerCase());

    const getVariantError = (variant: CsvMiniModelVariantViewModel, field: string) =>
        variant?.errors?.find((e) => e.field?.toLowerCase() === field.toLowerCase())?.error;

    const hasVariantError = (variant: CsvMiniModelVariantViewModel, field: string) =>
        variant?.errors?.some((e) => e.field?.toLowerCase() === field.toLowerCase());


    const handleNavigateItemImport = () => {
        navigate({ path: "/purchase/item/import/list" });

    };



    return (
        <>
            {isCompleted && showSuccesDialog && (
                <Dialog open fullWidth maxWidth="sm" onClose={() => { }}>
                    <DialogTitle sx={{ textAlign: "center" }}>
                        {t("dialog.importSuccessText")}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                            <CheckCircle sx={{ fontSize: 72, color: "success.main" }} />
                        </Box>
                        <DialogContentText align="center">
                            {t("dialog.importSuccessFlavor")}
                        </DialogContentText>
                    </DialogContent>

                    <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 3 }}>
                        <Button
                            onClick={() => navigate({ path: "/purchase/item/list" })}
                            variant="contained"
                            startIcon={<ListAltIcon />}
                        >
                            {t("dialog.importGoToList")}
                        </Button>

                        <Button
                            onClick={() => setShowSuccessDialog(false)}
                            variant="outlined"
                            startIcon={<CancelIcon />}
                            autoFocus
                        >
                            {t("dialog.importStay")}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            <CenteredCard title={t("common.itemImport")} isLoading={isLoading} >
                <Box sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    maxWidth: "fit-content",
                    mb: 2
                }}>
                    <DefaultActions

                        onDelete={handleDelete}
                        onImport={!viewModel?.createNew ? handleImportFromPreview : undefined}
                        customActions={[
                            { label: t("common.importList"), onClick: handleNavigateItemImport }
                        ]}

                    />
                </Box>

                {viewModel && (
                    <>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    label={"Id"}
                                    value={viewModel.id}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    margin="normal"
                                    disabled={true}

                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    InputLabelProps={{ shrink: true }}
                                    label={t("common.description")}
                                    value={viewModel.description || ""}
                                    onChange={(e) => handleChange("description", e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    error={!viewModel.description || viewModel.description.trim() === ""}
                                    helperText={!viewModel.description || viewModel.description.trim() === "" ? t("help.descriptionRequired") : ""}
                                    disabled={!viewModel?.createNew}
                                />
                            </Grid>
                            {viewModel.createNew && (
                                <>
                                    <Grid item xs={12}>

                                        <InsertDriveFileIcon fontSize="small" sx={{ mr: 1 }} />
                                        <Link href="/assets/Bex_ItemImport_Template.csv" download underline="hover">
                                            Bex_ItemImport_Template.csv
                                        </Link>

                                    </Grid>
                                    {pendingFile != null && (
                                        <Grid item xs={12}>
                                            <TextField
                                                InputLabelProps={{ shrink: true }}
                                                label={t("common.fileName")}
                                                value={pendingFile.name}

                                                fullWidth
                                                margin="normal"
                                                disabled={true}
                                            />
                                        </Grid>
                                    )}
                                    <Grid item xs={12}>
                                        <Button
                                            variant="contained"
                                            component="label"
                                            fullWidth
                                            color="info"
                                        >
                                            {t("common.uploadFile")}
                                            <input
                                                type="file"
                                                accept=".csv,text/csv"
                                                hidden
                                                onChange={handleFileChange}
                                            />
                                        </Button>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Button
                                            variant="contained"
                                            component="label"
                                            fullWidth
                                            onClick={handleImport}
                                            color="success"
                                        >
                                            {t("common.import")}

                                        </Button>
                                    </Grid>
                                </>


                            )}

                            {!viewModel.createNew && (
                                <>
                                    <Grid item xs={12}>
                                        <Button
                                            variant="contained"
                                            component="label"
                                            fullWidth
                                            onClick={handleImportFromPreview}
                                            color="success"
                                        >
                                            {t("common.import")}

                                        </Button>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            variant="contained"
                                            component="label"
                                            fullWidth
                                            onClick={() => setShowOnlyErrorLines(prev => !prev)}
                                            color={showOnlyErrorLines ? "info" : "error"}
                                        >
                                            {showOnlyErrorLines ? t("common.showAll") : t("common.showOnlyErrors")}
                                        </Button>
                                    </Grid>
                                </>
                            )}

                        </Grid>


                    </>
                )}



                {!viewModel?.createNew && (
                    <>
                        <Paper sx={{ padding: 2 }}>
                            {/* Search Field */}

                            {/* Card Layout for Items */}
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                {importLines.map((item) => (
                                    <Grid item xs={12} key={item.no} >

                                        <Grid container spacing={1} sx={{
                                            p: 1.5,
                                            border: '1px solid #ccc',
                                            borderRadius: 1,
                                            cursor: "pointer",
                                            transition: "all 0.2s ease-in-out",
                                            position: "relative",
                                            backgroundColor: item.containsErrors ? 'rgba(255, 0, 0, 0.05)' : (item.completed ? 'rgba(0, 255, 0, 0.05)' : undefined),
                                            ':hover': {
                                                backgroundColor: item.containsErrors ? 'rgba(255, 0, 0, 0.08)' : (item.completed ? 'rgba(0, 255, 0, 0.05)' : '#f9f9f9')
                                            }
                                        }} onClick={() => setEditingLine(item)}>
                                            {item.containsErrors && (
                                                <Grid item xs={12}>
                                                    <Box display="flex" alignItems="center" color="error.main">
                                                        <WarningAmberIcon sx={{ mr: 1 }} />
                                                        <Typography variant="body2">{t("help.importRowHasErrors")}</Typography>
                                                    </Box>
                                                </Grid>
                                            )}
                                            {item.completed && (
                                                <CheckCircle
                                                    sx={{
                                                        position: "absolute",
                                                        top: 8,       // distance from inside edge
                                                        right: 8,     // distance from inside edge
                                                        color: "success.main",
                                                        fontSize: 24
                                                    }}
                                                />
                                            )}
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle1" fontWeight="bold">{item.no}</Typography>
                                            </Grid>


                                            <Grid item xs={6} md={4}>
                                                <Typography variant="body2"> {t("common.description")}:</Typography>
                                                <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                                    {item.description}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={6} md={4}>
                                                <Typography variant="body2"> {t("common.itemGroup")}:</Typography>
                                                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{item.itemGroup}</Typography>
                                            </Grid>

                                            <Grid item xs={6} md={4}>
                                                <Typography variant="body2"> {t("common.itemCategory")}:</Typography>
                                                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{item.itemCategory}</Typography>
                                            </Grid>

                                            <Grid item xs={6} md={4}>
                                                <Typography variant="body2"> {t("common.productGroup")}:</Typography>
                                                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{item.productGroup}</Typography>
                                            </Grid>

                                            <Grid item xs={6} md={4}>
                                                <Typography variant="body2"> {t("common.program")}:</Typography>
                                                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{item.program}</Typography>
                                            </Grid>

                                            <Grid item xs={6} md={4}>
                                                <Typography variant="body2"> {t("common.collection")}:</Typography>
                                                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{item.collection}</Typography>
                                            </Grid>



                                        </Grid>
                                    </Grid>

                                ))}
                                {importLines.length === 0 && (
                                    <Grid item xs={12}>
                                        <Typography align="center">{t("common.noItemsFound")}</Typography>
                                    </Grid>
                                )}
                            </Grid>
                            <ResponsivePagination

                                page={query.page}

                                pageSize={query.pageSize}

                                totalCount={totalCount}

                                onPageChange={handlePageChange}

                            />
                        </Paper>
                    </>


                )}

          <Box sx={{ marginTop: 2, textAlign: "center" }}>
            {/*<Button variant="contained" onClick={handleSave} disabled={isLoading} sx={{ marginRight: 2 }}>*/}
            {/*    {t("common.save")}*/}
            {/*</Button>*/}
            <Button variant="outlined" onClick={goBack}>
              {t("common.back")}
            </Button>
          </Box>
            </CenteredCard>

            <Dialog open={!!editingLine} onClose={() => setEditingLine(null)}>
                <DialogTitle sx={{ textAlign: "center" }}>{t("common.edit")}</DialogTitle>
                <DialogContent>
                    <Box sx={{

                    }}>

                        <Button fullWidth onClick={handleSaveLine} variant="contained" startIcon={<SaveIcon />} disabled={!editingLine}>
                            {t("common.save")}
                        </Button>


                        {editingLine && (
                            <>

                                <Grid container spacing={2}>

                                    <Grid item xs={12} lg={12} sx={{ marginTop: "1em" }}>

                                        <TextField
                                            InputLabelProps={{ shrink: true }}
                                            label={t("common.number")}
                                            variant="outlined"
                                            fullWidth
                                            value={editingLine?.no || ""}
                                            onChange={(e) => handleFieldChange("no", e.target.value)}
                                            error={hasError("no")}
                                            helperText={getError("no")}
                                        />

                                    </Grid>


                                    <Grid item xs={12} lg={12}>

                                        <TextField
                                            InputLabelProps={{ shrink: true }}
                                            label={t("common.description")}
                                            variant="outlined"
                                            fullWidth
                                            value={editingLine?.description || ""}
                                            onChange={(e) => handleFieldChange("description", e.target.value)}
                                            error={hasError("description")}
                                            helperText={getError("description")}
                                        />

                                    </Grid>

                                    {editingLine?.imageLink && (
                                        <Grid item xs={12} lg={12}>
                                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                                                <ImageBox
                                                    imageUrl={editingLine.imageLink ?? ""}
                                                    altText={editingLine.description ?? ""}
                                                    fallbackText="No Image"
                                                />

                                            </Box>
                                        </Grid>
                                    )}
                                    <Grid item xs={12} lg={12}>

                                        <TextField
                                            InputLabelProps={{ shrink: true }}
                                            label={t("common.image")}
                                            variant="outlined"
                                            fullWidth
                                            value={editingLine?.imageLink || ""}
                                            onChange={(e) => handleFieldChange("imageLink", e.target.value)}
                                            error={hasError("imagelink")}
                                            helperText={getError("imagelink")}
                                        />

                                    </Grid>


                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={showMiscDescriptions}
                                                    onChange={(e) => setShowMiscDescription(e.target.checked)}
                                                />
                                            }
                                            label={t("common.showDescriptions")}
                                        />
                                    </Grid>
                                    {showMiscDescriptions && (
                                        <>
                                            <Box
                                                sx={{
                                                    backgroundColor: "secondary.main",
                                                    padding: 2,
                                                    borderRadius: 2,
                                                    width: "100%"
                                                }}
                                            >
                                                <Grid item xs={12} lg={12}>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} lg={12}>
                                                            <TextField
                                                                InputLabelProps={{ shrink: true }}
                                                                label={t("common.description2")}
                                                                variant="outlined"
                                                                fullWidth
                                                                value={editingLine?.description2 || ""}
                                                                onChange={(e) => handleFieldChange("description2", e.target.value)}
                                                                error={hasError("description2")}
                                                                helperText={getError("description2")}
                                                            />

                                                        </Grid>
                                                        <Grid item xs={12} lg={12}>

                                                            <TextField
                                                                InputLabelProps={{ shrink: true }}
                                                                label={t("common.fullDescription")}
                                                                variant="outlined"
                                                                fullWidth
                                                                value={editingLine?.fullDescription || ""}
                                                                onChange={(e) => handleFieldChange("fullDescription", e.target.value)}
                                                                error={hasError("fulldescription")}
                                                                helperText={getError("fulldescription")}
                                                            />

                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </>
                                    )}
                                    {editingLine.variants?.length == 0 && (
                                        <Grid item xs={12} lg={12}>

                                            <TextField
                                                InputLabelProps={{ shrink: true }}
                                                label={t("common.barcode")}
                                                variant="outlined"
                                                fullWidth
                                                value={editingLine?.barcode || ""}
                                                onChange={(e) => handleFieldChange("barcode", e.target.value)}
                                                error={hasError("barcode")}
                                                helperText={getError("barcode")}
                                            />

                                        </Grid>
                                    )}
                                    <Grid item xs={12}>
                                        <Divider sx={{ mt: 2, mb: 1 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                {t("common.prices")}
                                            </Typography>
                                        </Divider>
                                    </Grid>
                                    <Grid item xs={12} lg={12}>
                                        <LocalizedNumberInputs
                                            label={t("common.salesPrice")}
                                            value={editingLine.salesPrice ?? null}
                                            onValueChange={(value) => {
                                                handleFieldChange("salesPrice", value ?? null);
                                            }}
                                            allowNegative={false} // Prevents negative numbers
                                            decimalScale={4} // Ensures max 4 decimals
                                            thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                            decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                                            error={hasError("salesprice")}
                                            helperText={getError("salesprice")}
                                        />
                                    </Grid>


                                    <Grid item xs={12} lg={12}>

                                        <TextField
                                            InputLabelProps={{ shrink: true }}
                                            label={t("common.salesPriceCurrency")}
                                            variant="outlined"
                                            fullWidth
                                            value={editingLine?.salesPriceCurrency || ""}
                                            onChange={(e) => handleFieldChange("salesPriceCurrency", e.target.value)}
                                            error={hasError("salespricecurrency")}
                                            helperText={getError("salespricecurrency")}

                                        />

                                    </Grid>


                                    <Grid item xs={12} lg={12}>
                                        <LocalizedNumberInputs
                                            label={t("common.purchasePrice")}
                                            value={editingLine.purchasePrice ?? null}
                                            onValueChange={(value) => {
                                                handleFieldChange("purchasePrice", value ?? null);
                                            }}
                                            allowNegative={false} // Prevents negative numbers
                                            decimalScale={4} // Ensures max 4 decimals
                                            thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                            decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                                            error={hasError("purchaseprice")}
                                            helperText={getError("purchaseprice")}
                                        />
                                    </Grid>


                                    <Grid item xs={12} lg={12}>

                                        <TextField
                                            InputLabelProps={{ shrink: true }}
                                            label={t("common.purchasePriceCurrency")}
                                            variant="outlined"
                                            fullWidth
                                            value={editingLine?.purchasePriceCurrency || ""}
                                            onChange={(e) => handleFieldChange("purchasePriceCurrency", e.target.value)}
                                            error={hasError("purchasepricecurrency")}
                                            helperText={getError("purchasepricecurrency")}

                                        />

                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={<Switch checked={showMorePrices} onChange={(e) => setShowMorePrices(e.target.checked)} />}
                                            label={`${t("common.moreSalesPrices")}`}
                                        />
                                    </Grid>
                                    {showMorePrices && (
                                        <>
                                            <Box
                                                sx={{
                                                    backgroundColor: "secondary.main",
                                                    padding: 2,
                                                    borderRadius: 2,
                                                    width: "100%"
                                                }}
                                            >
                                                <Grid container spacing={2}>
                                                 <Grid item xs={12} lg={12}>

                                                <LocalizedNumberInputs
                                                    label={t("common.salesPrice") + " 2"}
                                                    value={editingLine?.salesPrice_2 || null}
                                                    onValueChange={(value) => {
                                                        handleFieldChange("salesPrice_2", value ?? null);
                                                    }}
                                                    allowNegative={false} // Prevents negative numbers
                                                    decimalScale={4} // Ensures max 4 decimals
                                                    thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                                    decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                                                    error={hasError("salesPrice_2")}
                                                    helperText={getError("salesPrice_2")}
                                                />
                                            </Grid>
                                                 <Grid item xs={12} lg={12}>
                                                <TextField
                                                    InputLabelProps={{ shrink: true }}
                                                    label={t("common.salesPriceCurrency") +" 2"}
                                                    variant="outlined"
                                                    fullWidth
                                                    value={editingLine?.salesPriceCurrencyCode_2 || ""}
                                                    onChange={(e) => handleFieldChange("salesPriceCurrencyCode_2", e.target.value)}
                                                    error={hasError("salesPriceCurrencyCode_2")}
                                                    helperText={getError("salesPriceCurrencyCode_2")}

                                                />
                                            </Grid>

                                                 <Grid item xs={12} lg={12}>

                                                <LocalizedNumberInputs
                                                    label={t("common.salesPrice") + " 3"}
                                                    value={editingLine?.salesPrice_3 || null}
                                                    onValueChange={(value) => {
                                                        handleFieldChange("salesPrice_3", value ?? null);
                                                    }}
                                                    allowNegative={false} // Prevents negative numbers
                                                    decimalScale={4} // Ensures max 4 decimals
                                                    thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                                    decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                                                    error={hasError("salesPrice_3")}
                                                    helperText={getError("salesPrice_3")}
                                                />
                                            </Grid>
                                                 <Grid item xs={12} lg={12}>
                                                <TextField
                                                    InputLabelProps={{ shrink: true }}
                                                    label={t("common.salesPriceCurrency") + " 3"}
                                                    variant="outlined"
                                                    fullWidth
                                                    value={editingLine?.salesPriceCurrencyCode_3 || ""}
                                                    onChange={(e) => handleFieldChange("salesPriceCurrencyCode_3", e.target.value)}
                                                    error={hasError("salesPriceCurrencyCode_3")}
                                                    helperText={getError("salesPriceCurrencyCode_3")}

                                                />
                                            </Grid>
                                                 <Grid item xs={12} lg={12}>

                                                <LocalizedNumberInputs
                                                    label={t("common.salesPrice") + " 4"}
                                                    value={editingLine?.salesPrice_4 || null}
                                                    onValueChange={(value) => {
                                                        handleFieldChange("salesPrice_4", value ?? null);
                                                    }}
                                                    allowNegative={false} // Prevents negative numbers
                                                    decimalScale={4} // Ensures max 4 decimals
                                                    thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                                    decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                                                    error={hasError("salesPrice_4")}
                                                    helperText={getError("salesPrice_4")}
                                                />
                                            </Grid>
                                                 <Grid item xs={12} lg={12}>
                                                <TextField
                                                    InputLabelProps={{ shrink: true }}
                                                    label={t("common.salesPriceCurrency") + " 4"}
                                                    variant="outlined"
                                                    fullWidth
                                                    value={editingLine?.salesPriceCurrencyCode_4 || ""}
                                                    onChange={(e) => handleFieldChange("salesPriceCurrencyCode_4", e.target.value)}
                                                    error={hasError("salesPriceCurrencyCode_4")}
                                                    helperText={getError("salesPriceCurrencyCode_4")}

                                                />
                                            </Grid>
                                                 <Grid item xs={12} lg={12}>

                                                <LocalizedNumberInputs
                                                    label={t("common.salesPrice") + " 5"}
                                                    value={editingLine?.salesPrice_5 || null}
                                                    onValueChange={(value) => {
                                                        handleFieldChange("salesPrice_5", value ?? null);
                                                    }}
                                                    allowNegative={false} // Prevents negative numbers
                                                    decimalScale={4} // Ensures max 4 decimals
                                                    thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                                    decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                                                    error={hasError("salesPrice_5")}
                                                    helperText={getError("salesPrice_5")}
                                                />
                                            </Grid>
                                                 <Grid item xs={12} lg={12}>
                                                <TextField
                                                    InputLabelProps={{ shrink: true }}
                                                    label={t("common.salesPriceCurrency") + " 5"}
                                                    variant="outlined"
                                                    fullWidth
                                                    value={editingLine?.salesPriceCurrencyCode_5 || ""}
                                                    onChange={(e) => handleFieldChange("salesPriceCurrencyCode_5", e.target.value)}
                                                    error={hasError("salesPriceCurrencyCode_5")}
                                                    helperText={getError("salesPriceCurrencyCode_5")}

                                                />
                                            </Grid>
                                                </Grid>
                                           </Box>
                                        </>
                                    )}


                                    <Grid item xs={12} lg={12}>

                                        <TextField
                                            InputLabelProps={{ shrink: true }}
                                            label={t("common.supplier")}
                                            variant="outlined"
                                            fullWidth
                                            value={editingLine?.supplier || ""}
                                            onChange={(e) => handleFieldChange("supplier", e.target.value)}
                                            error={hasError("supplier")}
                                            helperText={getError("supplier")}

                                        />

                                    </Grid>


                                    <Grid item xs={12} lg={12}>

                                        <TextField
                                            InputLabelProps={{ shrink: true }}
                                            label={t("common.supplierItemNo")}
                                            variant="outlined"
                                            fullWidth
                                            value={editingLine?.supplierItemNo || ""}
                                            onChange={(e) => handleFieldChange("supplierItemNo", e.target.value)}
                                            error={hasError("supplieritemno")}
                                            helperText={getError("supplieritemno")}
                                        />

                                    </Grid>

                                    <Grid item xs={6}>
                                        <TextField
                                            InputLabelProps={{ shrink: true }}
                                            variant="outlined"
                                            fullWidth
                                            value={editingLine?.b2C || ""}
                                            onChange={(e) => handleFieldChange("b2C", e.target.value)}
                                            label={"B2C"}
                                            error={hasError("b2C")}
                                            helperText={getError("b2C")}
                                        />
                                    </Grid>


                                    <Grid item xs={6}>
                                        <TextField
                                            InputLabelProps={{ shrink: true }}
                                            variant="outlined"
                                            fullWidth
                                            value={editingLine?.b2B || ""}
                                            onChange={(e) => handleFieldChange("b2B", e.target.value)}
                                            label={"B2B"}
                                            error={hasError("b2B")}
                                            helperText={getError("b2B")}
                                        />


                                        {/*<FormControlLabel*/}
                                        {/*    control={*/}
                                        {/*        <Switch*/}
                                        {/*            checked={editingLine.b2B === "YES" || editingLine.b2B === "JA"}*/}
                                        {/*            onChange={(e) => {*/}
                                        {/*                const newValue = e.target.checked ? "YES" : "NO"; // or "JA" / "NEJ"*/}
                                        {/*                handleFieldChange("b2B", newValue);*/}
                                        {/*            }}*/}

                                        {/*        />*/}
                                        {/*    }*/}
                                        {/*    label={"B2B"}*/}
                                        {/*/>*/}
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={showCustoms}
                                                    onChange={(e) => setShowCustoms(e.target.checked)}
                                                />
                                            }
                                            label={t("common.showCustoms")}
                                        />
                                    </Grid>
                                    {showCustoms && (
                                        <>
                                            <Box
                                                sx={{
                                                    backgroundColor: "secondary.main",
                                                    padding: 2,
                                                    borderRadius: 2,
                                                    width: "100%"
                                                }}
                                            >
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} lg={12}>

                                                        <TextField
                                                            InputLabelProps={{ shrink: true }}
                                                            variant="outlined"
                                                            fullWidth
                                                            value={editingLine?.statNo || ""}
                                                            onChange={(e) => handleFieldChange("statNo", e.target.value)}
                                                            label={t("common.statNo")}
                                                            error={hasError("statno")}
                                                            helperText={getError("statno")}
                                                        />





                                                    </Grid>
                                                    <Grid item xs={12} lg={12}>
                                                        <TextField
                                                            InputLabelProps={{ shrink: true }}
                                                            variant="outlined"
                                                            fullWidth
                                                            value={editingLine?.countryOfOrigin || ""}
                                                            onChange={(e) => handleFieldChange("countryOfOrigin", e.target.value)}
                                                            label={t("common.countryOfOrigin")}
                                                            error={hasError("countryoforigin")}
                                                            helperText={getError("countryoforigin")}
                                                        />


                                                    </Grid>
                                                    <Grid item xs={12} lg={12}>
                                                        <LocalizedNumberInputs
                                                            label={t("common.netWeight")}
                                                            value={editingLine.netWeight ?? null}
                                                            onValueChange={(value) => {
                                                                handleFieldChange("netWeight", value ?? null);
                                                            }}
                                                            allowNegative={false} // Prevents negative numbers
                                                            decimalScale={4} // Ensures max 4 decimals
                                                            thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                                            decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                                                            error={hasError("netweight")}
                                                            helperText={getError("netweight")}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} lg={12}>
                                                        <LocalizedNumberInputs
                                                            label={t("common.grossWeight")}
                                                            value={editingLine.grossWeight ?? null}
                                                            onValueChange={(value) => {
                                                                handleFieldChange("grossWeight", value ?? null);
                                                            }}
                                                            allowNegative={false} // Prevents negative numbers
                                                            decimalScale={4} // Ensures max 4 decimals
                                                            thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                                            decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                                                            error={hasError("grossweight")}
                                                            helperText={getError("grossweight")}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} lg={12}>
                                                        <LocalizedNumberInputs
                                                            label={t("common.volume")}
                                                            value={editingLine.volume ?? null}
                                                            onValueChange={(value) => {
                                                                handleFieldChange("volume", value ?? null);
                                                            }}
                                                            allowNegative={false} // Prevents negative numbers
                                                            decimalScale={4} // Ensures max 4 decimals
                                                            thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                                            decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                                                            error={hasError("volume")}
                                                            helperText={getError("volume")}

                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </>
                                    )}
                                    {editingLine.variants?.length == 0 && (
                                        <Grid item xs={12} lg={12}>
                                            <LocalizedNumberInputs
                                                label={t("common.stock")}
                                                value={editingLine.stock ?? null}
                                                onValueChange={(value) => {
                                                    handleFieldChange("stock", value ?? null);
                                                }}
                                                allowNegative={false} // Prevents negative numbers
                                                decimalScale={4} // Ensures max 4 decimals
                                                thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                                decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                                                error={hasError("stock")}
                                                helperText={getError("stock")}

                                            />
                                        </Grid>
                                    )}
                                    <Grid item xs={12}>
                                        <Divider sx={{ mt: 2, mb: 1 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                {t("common.dimensions")}
                                            </Typography>
                                        </Divider>
                                    </Grid>
                                    <Grid item xs={12} lg={12}>

                                        <TextField
                                            InputLabelProps={{ shrink: true }}
                                            label={t("common.itemCategory")}
                                            variant="outlined"
                                            fullWidth
                                            value={editingLine?.itemCategory || ""}
                                            onChange={(e) => handleFieldChange("itemCategory", e.target.value)}
                                            error={hasError("itemcategory")}
                                            helperText={getError("itemcategory")}
                                        />

                                    </Grid>

                                    <Grid item xs={12} lg={12}>

                                        <TextField
                                            InputLabelProps={{ shrink: true }}
                                            label={t("common.productGroup")}
                                            variant="outlined"
                                            fullWidth
                                            value={editingLine?.productGroup || ""}
                                            onChange={(e) => handleFieldChange("productGroup", e.target.value)}
                                            error={hasError("productgroup")}
                                            helperText={getError("productgroup")}
                                        />

                                    </Grid>

                                    <Grid item xs={12} lg={12}>

                                        <TextField
                                            InputLabelProps={{ shrink: true }}
                                            label={t("common.itemGroup")}
                                            variant="outlined"
                                            fullWidth
                                            value={editingLine?.itemGroup || ""}
                                            onChange={(e) => handleFieldChange("itemGroup", e.target.value)}
                                            error={hasError("itemgroup")}
                                            helperText={getError("itemgroup")}
                                        />

                                    </Grid>
                                    <Grid item xs={12} lg={12}>

                                        <TextField
                                            InputLabelProps={{ shrink: true }}
                                            label={t("common.program")}
                                            variant="outlined"
                                            fullWidth
                                            value={editingLine?.program || ""}
                                            onChange={(e) => handleFieldChange("program", e.target.value)}
                                            error={hasError("program")}
                                            helperText={getError("program")}
                                        />

                                    </Grid>
                                    <Grid item xs={12} lg={12}>

                                        <TextField
                                            InputLabelProps={{ shrink: true }}
                                            label={t("common.collection")}
                                            variant="outlined"
                                            fullWidth
                                            value={editingLine?.collection || ""}
                                            onChange={(e) => handleFieldChange("collection", e.target.value)}
                                            error={hasError("collection")}
                                            helperText={getError("collection")}
                                        />

                                    </Grid>
                                    <Grid item xs={12} lg={12}>

                                        <TextField
                                            InputLabelProps={{ shrink: true }}
                                            label={t("common.gender")}
                                            variant="outlined"
                                            fullWidth
                                            value={editingLine?.gender || ""}
                                            onChange={(e) => handleFieldChange("gender", e.target.value)}
                                            error={hasError("gender")}
                                            helperText={getError("gender")}
                                        />

                                    </Grid>
                                    <Grid item xs={12}>
                                        <Divider sx={{ mt: 2, mb: 1 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                {t("common.variantInfo")}
                                            </Typography>
                                        </Divider>
                                    </Grid>
                                    <Grid item xs={12} lg={12}>

                                        <TextField
                                            InputLabelProps={{ shrink: true }}
                                            label={t("common.colourGroup")}
                                            variant="outlined"
                                            fullWidth
                                            value={editingLine?.colourGroup || ""}
                                            onChange={(e) => handleFieldChange("colourGroup", e.target.value)}
                                            error={hasError("colourgroup")}
                                            helperText={getError("colourgroup")}
                                        />

                                    </Grid>
                                    <Grid item xs={12} lg={12}>

                                        <TextField
                                            InputLabelProps={{ shrink: true }}
                                            label={t("common.sizeGroup")}
                                            variant="outlined"
                                            fullWidth
                                            value={editingLine?.sizeGroup || ""}
                                            onChange={(e) => handleFieldChange("sizeGroup", e.target.value)}
                                            error={hasError("sizegroup")}
                                            helperText={getError("sizegroup")}
                                        />

                                    </Grid>
                                    <Grid item xs={12} lg={12}>

                                        <TextField
                                            InputLabelProps={{ shrink: true }}
                                            label={t("common.lengthGroup")}
                                            variant="outlined"
                                            fullWidth
                                            value={editingLine?.lengthGroup || ""}
                                            onChange={(e) => handleFieldChange("lengthGroup", e.target.value)}
                                            error={hasError("lengthgroup")}
                                            helperText={getError("lengthgroup")}
                                        />

                                    </Grid>
                                    {(editingLine.variants?.length ?? 0) > 0 && (
                                        <>
                                            <Grid item xs={12}>
                                                <Divider sx={{ mt: 2, mb: 1 }}>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        {t("common.variants")} ({editingLine.variants?.length})
                                                    </Typography>
                                                </Divider>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={showVariants}
                                                            onChange={(e) => setShowVariants(e.target.checked)}
                                                        />
                                                    }
                                                    label={t("common.showVariants")}
                                                />
                                            </Grid>
                                            {showVariants && editingLine.variants?.map((variant, index) => (
                                                <>
                                                    <Grid
                                                        key={index}
                                                        container
                                                        spacing={2}
                                                        sx={{
                                                            backgroundColor: index % 2 === 0 ? "#f5f5f5" : "transparent",
                                                            borderRadius: 1,
                                                            p: 2,
                                                            mt: 1
                                                        }}
                                                    >
                                                        <Grid item xs={12} lg={12}>
                                                            <TextField
                                                                InputLabelProps={{ shrink: true }}
                                                                label={t("common.colour")}
                                                                variant="outlined"
                                                                key={index}
                                                                fullWidth
                                                                value={variant.colour || ""}
                                                                onChange={(e) =>
                                                                    handleVariantFieldChange(editingLine, index, "colour", e.target.value)
                                                                }
                                                                error={hasVariantError(variant, "colour")}
                                                                helperText={getVariantError(variant, "colour")}
                                                            />
                                                        </Grid>

                                                        <Grid item xs={12} lg={12}>
                                                            <TextField
                                                                InputLabelProps={{ shrink: true }}
                                                                label={t("common.size")}
                                                                variant="outlined"
                                                                key={index}
                                                                fullWidth
                                                                value={variant.size || ""}
                                                                onChange={(e) =>
                                                                    handleVariantFieldChange(editingLine, index, "size", e.target.value)
                                                                }
                                                                error={hasVariantError(variant, "size")}
                                                                helperText={getVariantError(variant, "size")}
                                                            />
                                                        </Grid>

                                                        <Grid item xs={12} lg={12}>
                                                            <TextField
                                                                InputLabelProps={{ shrink: true }}
                                                                label={t("common.length")}
                                                                variant="outlined"
                                                                key={index}
                                                                fullWidth
                                                                value={variant.length || ""}
                                                                onChange={(e) =>
                                                                    handleVariantFieldChange(editingLine, index, "length", e.target.value)
                                                                }
                                                                error={hasVariantError(variant, "length")}
                                                                helperText={getVariantError(variant, "length")}
                                                            />
                                                        </Grid>

                                                        <Grid item xs={12} lg={12}>
                                                            <TextField
                                                                InputLabelProps={{ shrink: true }}
                                                                label={t("common.barcode")}
                                                                variant="outlined"
                                                                key={index}
                                                                fullWidth
                                                                value={variant.barcode || ""}
                                                                onChange={(e) =>
                                                                    handleVariantFieldChange(editingLine, index, "barcode", e.target.value)
                                                                }
                                                                error={hasVariantError(variant, "barcode")}
                                                                helperText={getVariantError(variant, "barcode")}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} lg={12}>
                                                            <LocalizedNumberInputs
                                                                label={t("common.stock")}
                                                                value={variant.stock ?? null}
                                                                onValueChange={(value) => {
                                                                    handleVariantFieldChange(editingLine, index, "stock", value ?? null);
                                                                }}
                                                                allowNegative={false} // Prevents negative numbers
                                                                decimalScale={4} // Ensures max 4 decimals
                                                                thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                                                decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                                                                error={hasVariantError(variant, "stock")}
                                                                helperText={getVariantError(variant, "stock")}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Divider sx={{ mt: 2, mb: 1 }}>
                                                            </Divider>
                                                        </Grid>
                                                    </Grid>
                                                </>
                                            ))}
                                        </>
                                    )}
                                    {editingLine.containsErrors && (
                                        <>
                                            <Typography variant="h6" color="error" gutterBottom sx={{ mt: 2 }}>
                                                {t("common.foundErrors")}
                                            </Typography>

                                            {editingLine.errors?.map((error, index) => (
                                                <Grid
                                                    key={index}
                                                    container
                                                    spacing={2}
                                                    sx={{
                                                        backgroundColor: index % 2 === 0 ? "#ffebee" : "#ffcdd2",
                                                        borderRadius: 1,
                                                        p: 2,
                                                        mt: 1,
                                                    }}
                                                >
                                                    <Grid item xs={12}>
                                                        <Divider sx={{ mb: 1 }} />
                                                        <Typography variant="body2">
                                                            <strong>{error.field}</strong>: {error.error}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            ))}
                                        </>
                                    )}
                                </Grid>
                            </>

                        )}

                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
                            <Button onClick={handleSaveLine} variant="contained" startIcon={<SaveIcon />} disabled={!editingLine}>
                                {t("common.save")}
                            </Button>
                            <Button onClick={() => handleDeleteLine(editingLine)} variant="contained" color="error" startIcon={<DeleteIcon />} disabled={!editingLine}>
                                {t("common.delete")}
                            </Button>
                            <Button onClick={() => setEditingLine(null)} variant="outlined" startIcon={<CancelIcon />}>
                                {t("common.cancel")}
                            </Button>

                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ItemImportEditPage;